import React, { useState } from 'react';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import {
	useApproveRegistryMutation,
	ApproveRegistryInput,
	ApproveRegistryMutation
} from '@/graphql/__generated__/onboarding-legal';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import logger from '@/logger';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';

const ApproveRegistryModal: React.FC<{
	isOpen: boolean;
	onClose: () => void;
	clientId: string;
	onSuccess?: () => void;
}> = ({ isOpen, onClose, clientId, onSuccess }) => {
	const t = useTranslations('RegistryDetails');
	const [formData, setFormData] = useState<ApproveRegistryInput>({
		clientId: '',
		deliberation: ''
	});
	const [formErrors, setFormErrors] = useState<Record<keyof ApproveRegistryInput, string>>({
		clientId: '',
		deliberation: ''
	});

	const onboardingClient = useApolloClient(SCHEMAS.ONBOARDING_LEGAL);

	const [approveRegistry, { loading }] = useApproveRegistryMutation({
		client: onboardingClient,
		onCompleted: async (data: ApproveRegistryMutation) => {
			const approveResult = data.approveRegistry;
			if (approveResult?.successful) {
				logger.success('Registry approved successfully', approveResult);
				onClose();
				if (onSuccess) {
					onSuccess();
				}
			}
			if (approveResult?.mutationErrors && approveResult.mutationErrors.length > 0) {
				let hasFieldErrors = false;

				approveResult.mutationErrors.forEach((error) => {
					if (error) {
						Sentry.captureException(error);
						switch (error.__typename) {
							case 'InvalidInput':
								logger.error('Invalid input', error.field);
								if (error.field === 'deliberation') {
									setFormErrors((prev) => ({
										...prev,
										deliberation: error.message || t('DeliberationRequired')
									}));
									hasFieldErrors = true;
								}
								break;
							case 'ActionFailed':
								logger.error('Action failed', error.message);
								break;
							case 'InvalidAction':
								logger.error('Invalid action', error.message);
								break;
							default:
								logger.error('Unhandled mutation error', error);
								break;
						}
					}
				});

				if (!hasFieldErrors) {
					toast.error(t('ErrorApprovingRegistry'));
				}
			}
		},
		onError: (error) => {
			logger.error('Error approving registry', error);
			toast.error(t('ErrorApprovingRegistry'));
		}
	});

	// Set initial client ID
	React.useEffect(() => {
		if (clientId) {
			setFormData((prev) => ({
				...prev,
				clientId
			}));
		}
	}, [clientId]);

	const handleDeliberationChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		setFormData((prev) => ({
			...prev,
			deliberation: value
		}));

		if (formErrors.deliberation) {
			setFormErrors((prev) => ({
				...prev,
				deliberation: ''
			}));
		}
	};

	const handleApproveRegistry = async () => {
		setFormErrors({
			clientId: '',
			deliberation: ''
		});

		await approveRegistry({
			variables: {
				input: formData
			}
		});
	};

	const deliberationLength =
		t('DeliberationLength') + '. ' + formData.deliberation.length + '/3400';

	return (
		<ConfirmModal
			isOpen={isOpen}
			confirmActionText={t('Approve')}
			title={t('ApproveRegistryTitle')}
			onClose={onClose}
			onCancel={onClose}
			onConfirm={handleApproveRegistry}
			maxWidth={600}
			subtitle={t('ApproveRegistrySubtitle')}
			confirmBtnLoading={loading}
		>
			<div className="flex flex-col gap-4 py-4">
				<Typography size="md">{t('DeliberationLabel')}</Typography>
				<div className="flex flex-col gap-1">
					<TextArea
						rows={4}
						value={formData.deliberation}
						onChange={handleDeliberationChange}
						placeholder={t('EnterDeliberation')}
						error={!!formErrors.deliberation}
						hint={deliberationLength}
						maxChars={3400}
						strictMaxChars
					/>
					{formErrors.deliberation && (
						<Typography size="sm" color="error">
							{formErrors.deliberation}
						</Typography>
					)}
				</div>
			</div>
		</ConfirmModal>
	);
};

export default ApproveRegistryModal;
